import { useEffect, useState } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { toast } from 'sonner';
import { Loader2, CheckCircle2, AlertTriangle, AlertCircle, Sparkles } from 'lucide-react';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetBody,
  SheetFooter,
  SheetClose,
} from '@/components/ui/sheet';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { Eyebrow } from '@/components/ui/eyebrow';
import { Pill } from '@/components/ui/pill';
import {
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
} from '@/components/ui/select';
import { useAcordos } from '@/lib/hooks/entidades';
import {
  usePreflight,
  useCreatePrestacao,
  type PreflightResult,
} from '@/lib/hooks/prestacoes';
import { fmtBRL } from '@/lib/utils';

interface FormValues {
  acordoId: string;
  competencia: string;
}

interface Props {
  workspaceId: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

function defaultCompetencia(): string {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
}

export function PrestacaoDrawer({ workspaceId, open, onOpenChange }: Props) {
  const acordosQ = useAcordos(workspaceId);
  const preflightMut = usePreflight(workspaceId);
  const createMut = useCreatePrestacao(workspaceId);
  const [preflight, setPreflight] = useState<PreflightResult | null>(null);

  const {
    register,
    handleSubmit,
    reset,
    watch,
    control,
    formState: { errors, isSubmitting },
  } = useForm<FormValues>({
    defaultValues: { acordoId: '', competencia: defaultCompetencia() },
  });

  const competencia = watch('competencia');
  const acordoId = watch('acordoId');

  useEffect(() => {
    if (!open) {
      reset({ acordoId: '', competencia: defaultCompetencia() });
      setPreflight(null);
      preflightMut.reset();
    }
  }, [open, reset, preflightMut]);

  useEffect(() => {
    if (open && competencia && /^\d{4}-\d{2}$/.test(competencia)) {
      preflightMut.mutate(competencia, {
        onSuccess: (r) => setPreflight(r),
        onError: () => setPreflight(null),
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [competencia, open]);

  async function onSubmit(values: FormValues) {
    try {
      const r = await createMut.mutateAsync(values);
      if (r.prestacao.status === 'RASCUNHO') {
        toast.warning('Prestação criada como RASCUNHO — corrija os bloqueios antes de disparar.');
      } else {
        toast.success('Prestação criada · pronta para iniciar quando você quiser.');
      }
      onOpenChange(false);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Falha ao criar prestação.');
    }
  }

  const acordos = acordosQ.data?.items ?? [];
  const blockers = preflight?.issues.filter((i) => i.severity === 'block') ?? [];
  const warnings = preflight?.issues.filter((i) => i.severity === 'warn') ?? [];
  const canSubmit = !!acordoId && !!competencia && !preflightMut.isPending;

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent>
        <form onSubmit={handleSubmit(onSubmit)} className="flex flex-col flex-1 min-h-0">
          <SheetHeader
            eyebrow="Nova prestação"
            title="Iniciar prestação de contas"
            description="Validação prévia antes de compilar e disparar a automação no portal."
          />
          <SheetBody className="space-y-5">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="mb-1.5 block">Acordo</Label>
                <Controller
                  control={control}
                  name="acordoId"
                  rules={{ required: 'Selecione um acordo' }}
                  render={({ field }) => (
                    <Select value={field.value} onValueChange={field.onChange}>
                      <SelectTrigger>
                        <SelectValue placeholder="Selecione…" />
                      </SelectTrigger>
                      <SelectContent>
                        {acordos.map((a) => (
                          <SelectItem key={a.id} value={a.id}>
                            {a.numero} — {a.orgaoConcedente}
                          </SelectItem>
                        ))}
                        {acordos.length === 0 && (
                          <div className="px-3 py-2 text-xs text-fg-muted">
                            Cadastre um acordo em /configuracoes primeiro
                          </div>
                        )}
                      </SelectContent>
                    </Select>
                  )}
                />
                {errors.acordoId && (
                  <p className="text-[11px] text-warn mt-1">{errors.acordoId.message}</p>
                )}
              </div>
              <div>
                <Label htmlFor="competencia" className="mb-1.5 block">Competência</Label>
                <Input
                  id="competencia"
                  type="month"
                  {...register('competencia', {
                    required: 'Obrigatório',
                    pattern: { value: /^\d{4}-\d{2}$/, message: 'Formato YYYY-MM' },
                  })}
                />
                {errors.competencia && (
                  <p className="text-[11px] text-warn mt-1">{errors.competencia.message}</p>
                )}
              </div>
            </div>

            {/* Snapshot */}
            {preflightMut.isPending && (
              <div className="flex items-center gap-2 text-sm text-fg-muted">
                <Loader2 className="h-4 w-4 animate-spin" strokeWidth={1.8} />
                Carregando snapshot do mês…
              </div>
            )}
            {preflight && (
              <div className="space-y-3">
                <div>
                  <Eyebrow>Snapshot do mês</Eyebrow>
                  <div className="grid grid-cols-3 gap-2 mt-2">
                    <SnapshotPill
                      label="Pagamentos"
                      value={preflight.snapshot.pagamentos.count}
                      hint={fmtBRL(preflight.snapshot.pagamentos.total)}
                    />
                    <SnapshotPill
                      label="Receitas"
                      value={preflight.snapshot.receitas.count}
                      hint={fmtBRL(preflight.snapshot.receitas.total)}
                    />
                    <SnapshotPill
                      label="Empregados ativos"
                      value={preflight.snapshot.empregados.ativos}
                    />
                    <SnapshotPill label="Bens" value={preflight.snapshot.bens.count} />
                    <SnapshotPill label="Contratos" value={preflight.snapshot.contratos.count} />
                    <SnapshotPill
                      label="Doc. Fiscais"
                      value={preflight.snapshot.documentosFiscais.count}
                    />
                  </div>
                </div>

                {/* Issues */}
                {blockers.length > 0 && (
                  <div className="rounded-3 border border-warn/30 bg-warn-soft/40 p-4">
                    <div className="flex items-start gap-2 mb-2">
                      <AlertCircle className="h-4 w-4 text-warn shrink-0 mt-0.5" strokeWidth={1.8} />
                      <p className="text-sm font-semibold text-warn">
                        {blockers.length} bloqueio{blockers.length === 1 ? '' : 's'} — corrija antes de disparar
                      </p>
                    </div>
                    <ul className="space-y-1.5 ml-6">
                      {blockers.map((b, i) => (
                        <li key={i} className="text-sm text-fg flex gap-2">
                          <span className="text-warn">·</span>
                          <span>{b.message}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
                {warnings.length > 0 && (
                  <div className="rounded-3 border border-card-stroke bg-butter-soft/40 p-4">
                    <div className="flex items-start gap-2 mb-2">
                      <AlertTriangle className="h-4 w-4 text-butter-deep shrink-0 mt-0.5" strokeWidth={1.8} />
                      <p className="text-sm font-semibold text-butter-deep">
                        {warnings.length} alerta{warnings.length === 1 ? '' : 's'} — vale revisar
                      </p>
                    </div>
                    <ul className="space-y-1.5 ml-6">
                      {warnings.map((w, i) => (
                        <li key={i} className="text-sm text-fg-muted flex gap-2">
                          <span>·</span>
                          <span>{w.message}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
                {blockers.length === 0 && warnings.length === 0 && (
                  <div className="rounded-3 border border-mint-deep/20 bg-mint-soft/40 p-4 flex items-center gap-2">
                    <CheckCircle2 className="h-5 w-5 text-mint-deep shrink-0" strokeWidth={1.8} />
                    <p className="text-sm text-mint-deep">
                      Tudo pronto — sem bloqueios.
                    </p>
                  </div>
                )}

                <div className="rounded-2 bg-lilac-soft/30 border border-lilac-deep/20 p-3 flex items-start gap-2">
                  <Sparkles className="h-4 w-4 text-lilac-deep shrink-0 mt-0.5" strokeWidth={1.8} />
                  <p className="text-[12px] text-fg-muted">
                    Após criada, a automação só dispara quando Steel.dev e BullMQ estiverem
                    provisionados (próxima fase). Por enquanto, a prestação fica como{' '}
                    <Pill tone="mint" className="text-[10px] py-0.5">PRONTA</Pill> aguardando.
                  </p>
                </div>
              </div>
            )}
          </SheetBody>
          <SheetFooter>
            <SheetClose asChild>
              <Button variant="ghost" type="button">Cancelar</Button>
            </SheetClose>
            <Button
              variant="primary"
              type="submit"
              disabled={!canSubmit || isSubmitting}
            >
              {isSubmitting ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin" strokeWidth={1.8} />
                  Criando…
                </>
              ) : blockers.length > 0 ? (
                'Criar como rascunho'
              ) : (
                'Criar prestação'
              )}
            </Button>
          </SheetFooter>
        </form>
      </SheetContent>
    </Sheet>
  );
}

function SnapshotPill({
  label,
  value,
  hint,
}: {
  label: string;
  value: number;
  hint?: string;
}) {
  return (
    <div className="rounded-2 border border-card-stroke bg-card-soft p-2.5">
      <Eyebrow>{label}</Eyebrow>
      <p className="font-mono text-[18px] font-bold text-fg tabular-nums mt-0.5">{value}</p>
      {hint && <p className="text-[10px] text-fg-muted font-mono mt-0.5">{hint}</p>}
    </div>
  );
}
